import * as THREE from 'three';
import { PICK_MATERIAL } from '../core/materials.js';
import { HERRING, HERRING_FACTS } from '../config.js';

// ---------------------------------------------------------------------------
// Herring.
//
// A school is a patch of broken water somewhere out in the bay: white splashes
// that come and go on their own. Finding one is most of the game, so they are
// deliberately not signposted — no marker, no minimap, just something white
// glittering off the bow that was not there a minute ago.
//
// Every school is a single instanced mesh, so two schools cost two draw calls
// no matter how much water they churn up.
// ---------------------------------------------------------------------------

const SPLASHES = 16;

export class HerringSchools {
    /**
     * @param scene
     * @param isWater  (x, z) => boolean — where a school may appear
     * @param seaLevelY world Y of the waterline
     */
    constructor(scene, isWater, seaLevelY) {
        this.scene = scene;
        this.isWater = isWater;
        this.y = seaLevelY;
        this.schools = [];
        this.sinceSpawn = 0;
        // When the relay is up it owns the schools, so everyone sees the same
        // water. Offline, the client makes its own — otherwise a visitor who
        // cannot reach the relay gets a sea with no fish in it at all.
        this.served = false;
        this.night = 1;

        this.geometry = new THREE.BoxGeometry(0.42, 0.18, 0.42);
        this.material = new THREE.MeshBasicMaterial({
            color: 0xf2f7f8, transparent: true, opacity: 0.85, depthWrite: false,
        });
        this._localId = 0;
        this._m = new THREE.Matrix4();
        this._q = new THREE.Quaternion();
        this._s = new THREE.Vector3();
        this._p = new THREE.Vector3();
    }

    _spawnPoint() {
        // Twenty tries, then give up until the next tick rather than loop
        // forever looking for water that might not be there.
        for (let i = 0; i < 20; i++) {
            const a = Math.random() * Math.PI * 2;
            const r = HERRING.spawnNear + Math.random() * (HERRING.spawnFar - HERRING.spawnNear);
            const x = Math.cos(a) * r;
            const z = Math.sin(a) * r;
            if (!this.isWater(x, z)) continue;
            // Check the rim too, or a school lands half on a sandbank.
            if (!this.isWater(x + HERRING.radius, z) || !this.isWater(x - HERRING.radius, z)) continue;
            if (!this.isWater(x, z + HERRING.radius) || !this.isWater(x, z - HERRING.radius)) continue;
            return { x, z };
        }
        return null;
    }

    /** Build a school from the relay's description of it. */
    adopt(info) {
        if (this.schools.some((s) => s.id === info.id)) return null;
        return this._build({
            id: info.id,
            x: info.x,
            z: info.z,
            size: info.size,
            left: info.left,
            duration: info.duration,
            fact: HERRING_FACTS[info.fact % HERRING_FACTS.length],
        });
    }

    /** The relay says this school is thinner than we thought. */
    setLeft(id, left) {
        const s = this.schools.find((x) => x.id === id);
        if (s) s.left = left;
    }

    removeById(id) {
        const s = this.schools.find((x) => x.id === id);
        if (s) s.dying = true;
    }

    /** Everything the relay knows, on joining. */
    adoptAll(list) {
        this.served = true;
        for (const info of list || []) this.adopt(info);
        // Anything we invented while offline is not real to anyone else.
        for (const s of this.schools) if (s.id < 0) s.dying = true;
    }

    spawn() {
        const at = this._spawnPoint();
        if (!at) return null;

        const size = HERRING.sizeMin
            + Math.floor(Math.random() * (HERRING.sizeMax - HERRING.sizeMin + 1));
        return this._build({
            id: -(++this._localId),   // negative: ours alone
            x: at.x,
            z: at.z,
            size,
            left: size,
            duration: HERRING.fishSecondsMin
                + Math.random() * (HERRING.fishSecondsMax - HERRING.fishSecondsMin),
            fact: HERRING_FACTS[Math.floor(Math.random() * HERRING_FACTS.length)],
        });
    }

    _build({ id, x, z, size, left, duration, fact }) {
        const at = { x, z };
        // Its own material, so one school can brighten under the pointer
        // without the other one lighting up in sympathy.
        const material = this.material.clone();
        const mesh = new THREE.InstancedMesh(this.geometry, material, SPLASHES);
        mesh.frustumCulled = false;
        mesh.renderOrder = 3;

        const school = {
            id,
            x: at.x,
            z: at.z,
            size,
            fact,
            left,
            duration,
            life: HERRING.lifeMin + Math.random() * (HERRING.lifeMax - HERRING.lifeMin),
            age: 0,
            fade: 0,
            mesh,
            material,
            hi: 0,
            splashes: Array.from({ length: SPLASHES }, () => ({
                a: Math.random() * Math.PI * 2,
                r: Math.random() * HERRING.radius * 0.85,
                phase: Math.random() * Math.PI * 2,
                speed: 0.6 + Math.random() * 1.4,
            })),
        };

        mesh.position.set(at.x, this.y, at.z);
        this.scene.add(mesh);

        // Splashes are far too small and too intermittent to click, so the
        // whole patch of broken water is the target.
        const pick = new THREE.Mesh(
            new THREE.CylinderGeometry(HERRING.radius, HERRING.radius, 3, 12),
            PICK_MATERIAL,
        );
        pick.position.set(at.x, this.y + 1, at.z);
        pick.userData.school = school;
        school.pick = pick;
        this.scene.add(pick);
        this.schools.push(school);
        return school;
    }

    /** Pick volumes, for the pointer test. */
    pickTargets() {
        return this.schools.filter((s) => !s.dying).map((s) => s.pick);
    }

    /** Are they up? Herring surface after dark and sink again at dawn. */
    get dark() {
        return this.night > 0.45;
    }

    /** The school the boat is currently over, if any. */
    at(x, z) {
        for (const s of this.schools) {
            if (s.left <= 0 || s.dying) continue;
            if ((x - s.x) ** 2 + (z - s.z) ** 2 < HERRING.radius ** 2) return s;
        }
        return null;
    }

    remove(school) {
        school.dying = true;
    }

    /**
     * @param night 0..1 — herring rise to feed after dark, which is why the
     *   drift-net fleets sailed at night. By day they are down deep and there
     *   is nothing on the surface to find.
     */
    update(dt, { busy = false, hovered = null, night = 1 } = {}) {
        this.night = night;
        // --- spawning ---
        // No new schools while the water is full: two is the limit, so the
        // bay never turns into a fish market.
        this.sinceSpawn += dt;
        const every = 60 / HERRING.perMinute;
        if (!this.served
            && this.dark
            && this.schools.length < HERRING.maxSchools
            && this.sinceSpawn > every * (0.5 + Math.random())) {
            this.sinceSpawn = 0;
            this.spawn();
        }

        // --- living ---
        for (const s of this.schools) {
            s.age += dt;
            // Served schools die when the relay says so, not on our clock.
            if (!this.served && !busy && s.age > s.life) s.dying = true;
            // And they all go down at first light, fished or not.
            if (!this.dark && !busy) s.dying = true;
            if (s.left <= 0) s.dying = true;

            const want = s.dying ? 0 : 1;
            s.fade += (want - s.fade) * Math.min(1, dt * 1.6);

            // Under the pointer the water works harder: brighter splashes,
            // thrown a little higher. Enough to say "this is a thing" without
            // drawing a box round it.
            s.hi += ((s === hovered ? 1 : 0) - s.hi) * Math.min(1, dt * 8);
            s.material.opacity = (0.85 + 0.15 * s.hi) * s.fade;

            for (let i = 0; i < SPLASHES; i++) {
                const sp = s.splashes[i];
                sp.phase += dt * sp.speed;
                // Each splash breaks the surface, sinks, and comes back
                // somewhere else — the churn is what reads as fish.
                const beat = Math.sin(sp.phase);
                const up = Math.max(0, beat) ** 2;
                if (beat < -0.98) {
                    sp.a = Math.random() * Math.PI * 2;
                    sp.r = Math.random() * HERRING.radius * 0.85;
                }
                const scale = up * s.fade * (0.6 + 0.5 * (s.left / s.size)) * (1 + 0.4 * s.hi);
                this._p.set(Math.cos(sp.a) * sp.r, up * (0.5 + 0.45 * s.hi), Math.sin(sp.a) * sp.r);
                this._s.set(scale, scale, scale);
                this._m.compose(this._p, this._q, this._s);
                s.mesh.setMatrixAt(i, this._m);
            }
            s.mesh.instanceMatrix.needsUpdate = true;
        }

        // --- clearing away ---
        this.schools = this.schools.filter((s) => {
            if (s.dying && s.fade < 0.02) {
                this.scene.remove(s.mesh);
                this.scene.remove(s.pick);
                s.pick.geometry.dispose();
                s.material.dispose();
                s.mesh.dispose();
                return false;
            }
            return true;
        });
    }
}
